<template>
  <v-row no-gutters>
    <v-col cols="10" v-if="isFetched">
      <v-row no-gutters align="center" class="pb-3">
        <v-col cols="9">
          <v-row no-gutters align="center" style="gap: 1rem" class="mr-2">
            <v-col cols="auto">
              <v-row no-gutters style="gap: 0.5rem">
                <v-col cols="auto" style="display:flex; align-items: center; justify-content: center">
                  <v-icon color="rgba(0,0,0,0.7)">
                    mdi-calendar
                  </v-icon>
                </v-col>
                <v-col>
                  <data-input v-model="tableFilters.dateType" v-bind="dateTypeInput" />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="auto" style="flex-grow: 0; max-width: 10rem" v-for="(select, idx) of tableSelects" :key="idx">
              <data-input
                v-model="tableFilters[select.name]"
                v-bind="select.inputProps"
                class="table-select"
                :class="tableFilters[select.name].length > 0 ? 'table-select-selected' : 'table-select-empty'"
              >
                <template #selection></template>
                <template #prepend-inner>
                  <span
                    class="table-select-text"
                    :class="
                      tableFilters[select.name].length === 0 ? 'inactive-table-select-text' : 'active-table-select-text'
                    "
                  >
                    {{ select.label }}
                  </span>
                </template>
              </data-input>
            </v-col>
          </v-row>
        </v-col>
        <v-spacer />
        <v-col cols="3">
          <data-input v-model="tableFilters.searchInput" v-bind="searchTypeInput" />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="items"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 20],
            }"
            :server-items-length="totalItems"
            :options.sync="options"
            :sort-by.sync="sortBy"
            style="border: 1px solid rgba(0, 0, 0, 0.12)"
          >
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';
import { debounce } from 'lodash';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import { get } from 'vuex-pathify';

export default {
  name: 'ActivityLogTable',
  components: {
    DataInput,
    MainLoader,
  },
  data: () => ({
    isFetched: false,
    items: [],
    totalItems: 0,
    tableFilters: {
      users: [],
      activities: [],
      searchInput: null,
      dateType: [],
    },
    options: {
      itemsPerPage: 5,
      page: 1,
    },
    sortBy: ['date'],
    registeredActivities: {},
  }),
  computed: {
    users: get('users/users'),
    registeredActivitiesArray() {
      return Object.entries(this.registeredActivities).map(([key, name]) => {
        return {
          key,
          name,
        };
      });
    },
    tableSelects() {
      return [
        {
          name: 'activities',
          label: this.$i18n.t('admin.activityLog.activity_type'),
          inputProps: {
            itemText: 'name',
            dataType: {
              name: 'select',
            },
            hideDetails: true,
            multiple: true,
            autoConvertSelect: false,
            itemValue: 'key',
            items: this.registeredActivitiesArray,
          },
        },
        {
          name: 'users',
          label: this.$i18n.t('admin.activityLog.userType'),
          inputProps: {
            itemText: 'name',
            dataType: {
              name: 'select',
            },
            hideDetails: true,
            multiple: true,
            autoConvertSelect: false,
            itemValue: 'id',
            items: Object.values(this.users),
          },
        },
      ];
    },
    dateTypeInput() {
      return {
        dataType: { name: 'daterange' },
        label: this.$i18n.t('admin.activityLog.timestamp'),
        localeDateFormat: 'YYYY-MM-DD',
        returnType: 'date',
        hideDetails: true,
        clearable: true,
      };
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
    headers() {
      const inputs = ['id', 'user', 'ip_address', 'activity_type', 'date'];
      return [
        ...inputs.map(value => {
          return {
            text: this.$i18n.t(`admin.activityLog.${value}`),
            value: value,
            sortable: ['user', 'activity_type'].includes(value) ? false : true,
            align: 'center',
          };
        }),
      ];
    },
    getSearchedUsers() {
      return this.tableFilters.users.length > 0
        ? {
            $IN: {
              'events_activity_log.user': this.tableFilters.users,
            },
          }
        : null;
    },
    getSearchedActivities() {
      return this.tableFilters.activities.length > 0
        ? {
            $IN: {
              'events_activity_log.activity_type': this.tableFilters.activities,
            },
          }
        : null;
    },
    getSearchedItems() {
      return {
        '!OR': [
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_activity_log.ip_address': {
                value: this.tableFilters.searchInput || '',
              },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_activity_log.id': { value: this.tableFilters.searchInput || '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_activity_log.date': { value: this.tableFilters.searchInput || '' },
            },
          },
        ],
      };
    },
  },
  methods: {
    getActivitiesLog: call('admin/getActivitiesLog'),
    getActivitiesUniques: call('admin/getActivitiesUniques'),
    getUsers: call('users/getUsers'),
    async init() {
      const [registeredActivities] = await Promise.all([
        this.getActivitiesUniques(),
        this.getUsers(),
        this.fetchTable(),
      ]);
      this.registeredActivities = registeredActivities;
      if (this.users.length === 0);
    },
    async fetchTable() {
      const { page, itemsPerPage, sortBy = [], sortDesc = [] } = this.options;
      const filterArray = [
        ...(this.tableFilters.searchInput ? [this.getSearchedItems] : []),
        ...(this.getSearchedUsers ? [this.getSearchedUsers] : []),
        ...(this.getSearchedActivities ? [this.getSearchedActivities] : []),
        ...(this.tableFilters.dateType?.length === 2
          ? [
              {
                $GREATER_OR_EQUAL: {
                  'events_activity_log.date': { value: this.tableFilters.dateType[0] },
                },
              },
              {
                $LESS_OR_EQUAL: {
                  'events_activity_log.date': { value: `${this.tableFilters.dateType[1]}T23:59:59.999` },
                },
              },
            ]
          : []),
      ];
      const payload = {
        ...(filterArray.length > 0 && {
          features_filter: {
            '!AND': filterArray,
          },
        }),
        ...(sortBy.length === 1 && {
          sorting: [
            {
              attribute_name: sortBy[0],
              direction: sortDesc[0] ? 'asc' : 'desc',
            },
          ],
        }),
        params: {
          limit: itemsPerPage,
          page: page - 1,
          with_total_count: true,
        },
      };
      try {
        const r = await this.getActivitiesLog(payload);
        this.items = await r.features.features.map(el => {
          return {
            ...el.properties,
            id: el.id,
            user: el.properties.user === 1 ? this.$i18n.t('default.superadmin') : this.users[el.properties.user]?.name,
            activity_type: this.registeredActivities[el.properties.activity_type],
            date: el.properties.date.replace('T', ' ').slice(0, -4),
          };
        });
        this.totalItems = r.total_features_count;
        this.isFetched = true;
      } catch (error) {
        console.log('error:', error);
        this.isFetched = true;
      }
    },
  },
  mounted() {
    this.init();
    this.fetchTable = debounce(this.fetchTable, 275);
    this.$watch(
      vm => [vm.tableFilters, vm.options],
      () => {
        this.fetchTable();
      },
      {
        deep: true,
      }
    );
  },
};
</script>

<style lang="scss" scoped>
.table-select-selected ::v-deep .v-input {
  background-color: #e3effa;
  .v-input__icon .v-icon {
    color: #1976d2 !important;
  }
}
.table-select-empty ::v-deep .v-input {
  background-color: rgba(207, 207, 207, 0.15);
  .v-input__icon .v-icon {
    color: rgb(48, 48, 48) !important;
  }
}

.table-select-selected ::v-deep fieldset {
  border-color: #1976d2;
}

.table-select ::v-deep .v-input__prepend-inner {
  display: block;
  margin-right: 0.5rem !important;
  margin-top: auto !important;
  margin-bottom: auto !important;
}
.table-select {
  border-radius: 4px;
  .table-select-text {
    margin-top: auto;
    margin-bottom: auto;
  }
  .active-table-select-text {
    color: #1976d2;
  }
  .inactive-table-select-text {
    color: rgb(48, 48, 48);
  }
}
</style>
